import type { FormGroup, FormArray } from '@angular/forms';
import { FormControl, UntypedFormBuilder, ReactiveFormsModule } from '@angular/forms';
import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ChangeDetectorRef, inject } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import type { FormControlType, FormElement, Option, VariableData } from '@common/types/form-element.type';
import type { FormElementFormGroup, OptionFormArray } from '@common/types/form-element-forms.type';
import type { AddressBookVariable } from '@common/types/address-book.type';
import type { CrmPipeline } from '@integrations/crm-pipelines';
import type { CrmDeal } from '@integrations/crm-deals';
import type { CrmContact } from '@integrations/crm-contacts';
import type { TranslationsType } from '@common/constants/form-element-label.const';
import { controlsLabels } from '@common/constants/form-element-label.const';
import type { EHtmlAutocompleteAttr } from '@common/enums';
import { ESocialNetworks } from '@common/enums';
import {
    EAttributeTypesCrm,
    EContactFieldsCrm,
    ECrmCommentPlace,
    EFormControlType,
    EFormInputType,
    EVariablesTypes,
    EHtmlInputType,
} from '@common/enums';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import * as typeGuards from '@common/type-guards/form-elements.type-guards';

import { FormElementService } from '@common/services/form-element.service';
import { defaultFormElements } from '@common/constants/form-element.const';
import { DeviceMode } from '@common/types/layout.type';
import { NgIf, NgFor } from '@angular/common';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { SpCommonEditorComponent } from '../../sp-common-editor/src/sp-editor-common.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpSwitchComponent } from '../../../ui/sp-switch/src/sp-switch.component';

@Component({
    selector: 'sp-form-element-settings',
    templateUrl: './sp-form-element-settings.component.html',
    styleUrls: ['./sp-form-element-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        NgIf,
        BsDropdownModule,
        NgFor,
        SpCommonEditorComponent,
        TooltipModule,
        SpSwitchComponent,
        TranslateModule,
    ],
})
export class SpFormElementSettingsComponent implements OnInit, OnChanges, OnDestroy {
    private readonly fb = inject(UntypedFormBuilder);
    private readonly translate = inject(TranslateService);
    private readonly formElementService = inject(FormElementService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    @Input() public elementForm!: FormElementFormGroup | any;
    @Input() public elements!: FormElement[];
    @Input() public selectedChatBots!: FormArray<FormGroup<any>>;
    @Input() public addChatBotToForm: boolean = false;
    @Input() public isInitVariables = false;
    @Input() public variablesOptions: VariableData[][] = [];

    @Input() public addressBookVariablesList: AddressBookVariable[] = [];
    @Input() public crmContactsVariablesList: CrmContact[] = [];
    @Input() public crmDealsVariablesList: CrmDeal[] = [];
    @Input() public crmPipelinesList: CrmPipeline[] = [];

    @Input() public filteredFormControls: FormControlType[] = [];

    @Input() public isMainField: boolean = false;
    @Input() public isFormControlDisabled: boolean = false;
    @Input() public isCrmDealsListDisabled: boolean = false;
    @Input() public isCrmContactListDisabled: boolean = false;
    @Input() public isAddressBookListDisabled: boolean = false;
    @Input() public isShowProtectedList: boolean = true;
    @Input() public isEduForm: boolean = false;

    @Input() public baseHref: string = '';
    @Input() public deviceMode: DeviceMode = DeviceMode.desktop;

    @Input() public isPaymentEduForm: boolean = false;
    @Input() public isLoginEduForm: boolean = false;

    private readonly unsubscribe$ = new Subject<void>();

    public readonly compareVariables = (a: VariableData, b: VariableData) => {
        if (!a || !b) return a === b;
        return a._id === b._id;
    };
    public readonly typeGuards = typeGuards;
    public readonly formInputType = EFormInputType;
    public readonly formControlType = EFormControlType;
    public readonly attributeTypes = EAttributeTypesCrm;
    public readonly variableTypes = EVariablesTypes;
    public readonly controlsLabels = controlsLabels as TranslationsType;
    public readonly crmCommentPlaceArray = [
        ECrmCommentPlace.crmAllComment,
        ECrmCommentPlace.crmContactComment,
        ECrmCommentPlace.crmDealComment,
    ];

    @Input() public selectedVariables: (VariableData | null)[] = [];

    public selectedVariablesForm: FormArray = this.fb.array([]);

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedVariables']) {
            this.selectedVariablesForm.clear({ emitEvent: false });
            this.selectedVariables.forEach((variable) => {
                this.selectedVariablesForm.push(new FormControl(variable), { emitEvent: false });
            });
        }
    }

    public ngOnInit(): void {
        this.crmDealVariable.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
            if (!this.isCrmDealVariableSelect) {
                return;
            }

            this.setCrmDealSelectOptions();
        });

        this.crmContactVariable.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
            if (!this.isCrmContactVariableSelect) {
                return;
            }

            this.setCrmContactSelectOptions();
        });
        this.changeDetectorRef.detectChanges();
    }

    public filteredBotVariables(sn: ESocialNetworks, botVariables: VariableData[]): VariableData[] {
        let userVariables: VariableData[] = null;
        let selectedUserVariable: VariableData = null;

        switch (sn) {
            case ESocialNetworks.telegram:
                userVariables = this.elements.map((element: FormElement) => element.telegramVariable);
                selectedUserVariable = this.telegramVariable.value;
                break;
            case ESocialNetworks.instagram:
                userVariables = this.elements.map((element: FormElement) => element.instagramVariable);
                selectedUserVariable = this.instagramVariable.value;
                break;
            case ESocialNetworks.viber:
                userVariables = this.elements.map((element: FormElement) => element.viberVariable);
                selectedUserVariable = this.viberVariable.value;
                break;
            case ESocialNetworks.messenger:
                userVariables = this.elements.map((element: FormElement) => element.messengerVariable);
                selectedUserVariable = this.messengerVariable.value;
                break;
            case ESocialNetworks.whatsapp:
                userVariables = this.elements.map((element: FormElement) => element.whatsappVariable);
                selectedUserVariable = this.whatsappVariable.value;
                break;
            case ESocialNetworks.tiktok:
                userVariables = this.elements.map((element: FormElement) => element.tiktokVariable);
                selectedUserVariable = this.tiktokVariable.value;
                break;
            default:
                break;
        }

        const userNames = new Set(userVariables?.map((el) => el?.name));

        return botVariables
            ? [
                  ...botVariables.filter((variable: VariableData) => !userNames.has(variable.name)),
                  selectedUserVariable,
              ].filter(Boolean)
            : [];
    }

    public changeElementType(type: FormControlType | string): void {
        const element = this.formElementService.getDefaultElement(type);

        if (typeGuards.isRadio(element) || typeGuards.isSelect(element)) {
            this.elementForm.reset(element);
            this.elementForm.setControl(
                'options',
                this.fb.array(element.options.map((option) => this.fb.group(option)))
            );
        } else {
            this.elementForm.addControl('placeholder', new FormControl(''));
            this.elementForm.addControl('autocompleteType', new FormControl(''));
            this.elementForm.reset(element);
        }

        this.elementForm.markAsDirty();
    }

    public setCrmDealSelectOptions(): void {
        const selectedCrmDeal = this.getCrmDealVariableById(this.crmDealVariable.value as number);

        this.crmContactVariable.patchValue(null, { emitEvent: false });
        this.elementForm.setControl(
            'options',
            this.fb.array(
                selectedCrmDeal.options.map((option: string, index: number) =>
                    this.fb.group({ value: option, label: option, selected: index === 0 })
                )
            )
        );
    }

    public setCrmContactSelectOptions(): void {
        const selectedCrmContact = this.getCrmContactVariableById(this.crmContactVariable.value as number);

        this.crmDealVariable.patchValue(null, { emitEvent: false });
        this.elementForm.setControl(
            'options',
            this.fb.array(
                selectedCrmContact.options.map((option: string, index: number) =>
                    this.fb.group({ value: option, label: option, selected: index === 0 })
                )
            )
        );
    }

    public setDynamicPhonePlaceholder(value: boolean): void {
        this.isDynamicPlaceholderEnabled.setValue(value);
    }

    public getVariableName(id: string | number, type: EVariablesTypes): string {
        let variableName: string;
        switch (type) {
            case EVariablesTypes.crmContactAttribute:
                variableName =
                    this.crmContactsVariablesList.find((variable: CrmContact) => variable.id === id)?.name ||
                    id.toString();
                break;
            case EVariablesTypes.crmDealAttribute:
                variableName = this.crmDealsVariablesList.find((variable: CrmDeal) => variable.id === id)?.name;
                break;
            case EVariablesTypes.emailAddressBook:
                variableName =
                    this.addressBookVariablesList.find((variable: AddressBookVariable) => variable.variable_name === id)
                        ?.variable_name || id.toString(); // show id(name) for system variables
                break;
            default:
                break;
        }
        return variableName;
    }

    public toggleRequired(val: boolean): void {
        if (this.protected.value) {
            return;
        }

        this.required.patchValue(val);
    }

    public addRadioOption(options: OptionFormArray): void {
        const index = options.length;
        const label = this.translate.instant('form_radio_option');
        const value = this.translate.instant('form_radio_value');

        const option: Option = {
            label: `${label} ${index}`,
            value: `${value} ${index}`,
            selected: false,
        };

        options.push(this.fb.group(option));
    }

    public removeRadioOption(options: OptionFormArray, index: number, event: Event): void {
        event.stopPropagation();
        options.removeAt(index);

        const isAnySelected = options.controls.some((option) => (option.value as Option).selected);

        if (!isAnySelected) {
            options.controls[0].patchValue({ selected: true });
        }
    }

    public addSelectOption(options: OptionFormArray): void {
        const index = options.length;
        const label = this.translate.instant('form_radio_option');
        const value = this.translate.instant('form_radio_value');

        const option: Option = {
            label: `${label} ${index}`,
            value: `${value} ${index}`,
            selected: false,
        };

        options.push(this.fb.group(option));
    }

    public removeSelectOption(options: OptionFormArray, index: number, event: Event): void {
        event.stopPropagation();
        options.removeAt(index);

        const isAnySelected = options.controls.some((option) => (option.value as Option).selected);

        if (!isAnySelected) {
            options.controls[0].patchValue({ selected: true });
        }
    }

    public checkRadioOption(options: OptionFormArray, index: number): void {
        if (options.controls[index].value.selected) {
            options.controls[index].patchValue({ selected: false });
        } else {
            options.controls.forEach((option) => option.patchValue({ selected: false }));
            options.controls[index].patchValue({ selected: true });
        }
    }

    public checkSelectOption(options: OptionFormArray, index: number): void {
        if ((this.elementFormValue as any).htmlInputType === 'multiselect') {
            options.controls[index].patchValue({ selected: !options.controls[index].value.selected });
        } else {
            if (options.controls[index].value.selected) {
                options.controls[index].patchValue({ selected: false });
            } else {
                options.controls.forEach((option) => option.patchValue({ selected: false }));
                options.controls[index].patchValue({ selected: true });
            }
        }
    }

    public isProtectedFieldDisabled(type: string): boolean {
        return this.type.value === type;
    }

    private getCrmDealVariableById(id: CrmDeal['id']): CrmDeal {
        return this.crmDealsVariablesList.find((variable: CrmDeal) => variable.id === id);
    }

    private getCrmContactVariableById(id: CrmContact['id']): CrmContact {
        return this.crmContactsVariablesList.find((variable: CrmContact) => variable.id === id);
    }

    public disableCrmSelectVariable(variable: CrmContact): boolean {
        if (this.type.value !== 'select' && variable.type === this.attributeTypes.multiselect) {
            return true;
        }

        if (this.type.value === EFormInputType.date && variable.type !== this.attributeTypes.date) {
            return true;
        }

        if (this.type.value !== EFormInputType.date && variable.type === this.attributeTypes.date) {
            return true;
        }

        if (this.type.value === EFormInputType.number && variable.type !== this.attributeTypes.number) {
            return true;
        }

        if (
            this.type.value === EFormControlType.checkbox &&
            variable.type !== this.attributeTypes.checkbox &&
            variable.type !== this.attributeTypes.text
        ) {
            return true;
        }

        if (variable.type !== this.attributeTypes.select) {
            return false;
        }

        return !(this.typeGuards.isRadio(this.elementFormValue) || this.typeGuards.isSelect(this.elementFormValue));
    }

    public disableAddressBookVariable(variable: AddressBookVariable): boolean {
        if (this.type.value !== EFormInputType.date && variable.variable_type === EHtmlInputType.date) {
            return true;
        }
    }

    public changeCrmContactVariable() {
        const value = this.crmContactVariable.value;
        const type = this.crmContactsVariablesList.find(
            (variable) => variable.id === this.crmContactVariable.value
        )?.type;
        switch (type) {
            // case this.attributeTypes.date:
            //     this.changeElementType(EFormInputType.date);
            //     break;
            // case this.attributeTypes.number:
            //     this.changeElementType(EFormInputType.number);
            //     break;
            // case this.attributeTypes.checkbox:
            //     this.changeElementType(EFormControlType.checkbox);
            //     break;
            case this.attributeTypes.multiselect:
                this.changeElementType('multiselect');
                break;
            case this.attributeTypes.select:
                this.changeElementType(EFormControlType.select);
                break;
        }
        this.crmContactVariable.patchValue(value);
    }

    public changeCrmDealVariable() {
        const value = this.crmDealVariable.value;
        const type = this.crmDealsVariablesList.find((variable) => variable.id === this.crmDealVariable.value)?.type;

        switch (type) {
            // case this.attributeTypes.date:
            //     this.changeElementType(EFormInputType.date);
            //     break;
            // case this.attributeTypes.number:
            //     this.changeElementType(EFormInputType.number);
            //     break;
            // case this.attributeTypes.checkbox:
            //     this.changeElementType(EFormControlType.checkbox);
            //     break;
            case this.attributeTypes.multiselect:
                this.changeElementType('multiselect');
                break;
            case this.attributeTypes.select:
                this.changeElementType(EFormControlType.select);
                break;
        }
        this.crmDealVariable.patchValue(value);
    }

    public get defaultElements(): Map<string, FormElement> {
        const defaultFormElementsMap = new Map(defaultFormElements);
        defaultFormElementsMap.delete(EFormInputType.question);

        if (this.isLoginEduForm) {
            defaultFormElementsMap.delete(EFormControlType.textarea);
        }

        this.filteredFormControls.forEach((element: FormControlType) => {
            defaultFormElementsMap.delete(element);
        });

        return defaultFormElementsMap;
    }

    public get defaultProtectedElements(): Map<string, FormElement> {
        return new Map(
            [...defaultFormElements].filter(
                ([, v]) => v.type === EFormInputType.email || v.type === EFormInputType.phone
            )
        );
    }

    public get filteredContactFields() {
        const usedFields = this.elements.map((element: FormElement) => element.crmContactVariable);

        return Object.values(EContactFieldsCrm).filter((field: any) => !usedFields.includes(field));
    }

    public get filteredCrmContactsVariables(): CrmContact[] {
        const usedVariablesSet = new Set(this.elements.map((element: FormElement) => element.crmContactVariable));

        return this.crmContactsVariablesList.filter((field: CrmContact) => !usedVariablesSet.has(field.id));
    }

    public get filteredCrmDealsVariables(): CrmDeal[] {
        const userVariablesSet = new Set(this.elements.map((element: FormElement) => element.crmDealVariable));

        return this.crmDealsVariablesList
            ? this.crmDealsVariablesList.filter((variable: CrmDeal) => !userVariablesSet.has(variable.id))
            : [];
    }

    public get filteredAddressBookVariables(): AddressBookVariable[] {
        const usedVariables = this.elements.map((element: FormElement) => element.variable);

        return this.addressBookVariablesList
            ? this.addressBookVariablesList.filter(
                  (variable: AddressBookVariable) => !usedVariables.includes(variable.variable_name)
              )
            : [];
    }

    public get isCrmDealVariableSelect(): boolean {
        if (!this.crmDealVariable.value) {
            return false;
        }

        const selectedCrmDeal = this.getCrmDealVariableById(this.crmDealVariable.value as number);
        if (!selectedCrmDeal) {
            return false;
        }

        return (
            selectedCrmDeal.type === this.attributeTypes.select ||
            selectedCrmDeal.type === this.attributeTypes.multiselect
        );
    }

    public get isCrmContactVariableSelect(): boolean {
        if (!this.crmContactVariable.value) {
            return false;
        }

        const selectedCrmContact = this.getCrmContactVariableById(this.crmContactVariable.value as number);
        if (!selectedCrmContact) {
            return false;
        }

        return (
            selectedCrmContact.type === this.attributeTypes.select ||
            selectedCrmContact.type === this.attributeTypes.multiselect
        );
    }

    public get showMakeRequiredPredicate(): boolean {
        return (
            this.type.value !== this.formInputType.email &&
            this.type.value !== this.formInputType.password &&
            this.type.value !== this.formInputType.phone &&
            this.type.value !== this.formInputType.hidden &&
            !this.isCrmDealVariableSelect
        );
    }

    public get label(): FormControl<FormElement['label']> {
        return this.elementForm.get('label') as FormControl<FormElement['label']>;
    }

    public get protected(): FormControl<FormElement['protected']> {
        return this.elementForm.get('protected') as FormControl<FormElement['protected']>;
    }

    public get required(): FormControl<FormElement['required']> {
        return this.elementForm.get('required') as FormControl<FormElement['required']>;
    }

    public get isDynamicPlaceholderEnabled(): FormControl<FormElement['isDynamicPlaceholderEnabled']> {
        return this.elementForm.get('isDynamicPlaceholderEnabled') as FormControl<
            FormElement['isDynamicPlaceholderEnabled']
        >;
    }

    public get variable(): FormControl<FormElement['variable']> {
        return this.elementForm.get('variable') as FormControl<FormElement['variable']>;
    }

    public get crmContactVariable(): FormControl<FormElement['crmContactVariable']> {
        return this.elementForm.get('crmContactVariable') as FormControl<FormElement['crmContactVariable']>;
    }

    public get crmDealVariable(): FormControl<FormElement['crmDealVariable']> {
        return this.elementForm.get('crmDealVariable') as FormControl<FormElement['crmDealVariable']>;
    }

    public get telegramVariable(): FormControl<FormElement['telegramVariable']> {
        return this.elementForm.get('telegramVariable') as FormControl<FormElement['telegramVariable']>;
    }

    public get instagramVariable(): FormControl<FormElement['instagramVariable']> {
        return this.elementForm.get('instagramVariable') as FormControl<FormElement['instagramVariable']>;
    }

    public get viberVariable(): FormControl<FormElement['viberVariable']> {
        return this.elementForm.get('viberVariable') as FormControl<FormElement['viberVariable']>;
    }

    public get messengerVariable(): FormControl<FormElement['messengerVariable']> {
        return this.elementForm.get('messengerVariable') as FormControl<FormElement['messengerVariable']>;
    }

    public get whatsappVariable(): FormControl<FormElement['whatsappVariable']> {
        return this.elementForm.get('whatsappVariable') as FormControl<FormElement['whatsappVariable']>;
    }

    public get tiktokVariable(): FormControl<FormElement['tiktokVariable']> {
        return this.elementForm.get('tiktokVariable') as FormControl<FormElement['tiktokVariable']>;
    }

    public get crmCommentPlace(): FormControl<ECrmCommentPlace> {
        return this.elementForm.get('crmCommentPlace') as FormControl<ECrmCommentPlace>;
    }

    public get type(): FormControl<FormElement['type']> {
        return this.elementForm.get('type') as FormControl<FormElement['type']>;
    }

    public get htmlInputType(): FormControl<EHtmlInputType> {
        return this.elementForm.get('htmlInputType') as FormControl<EHtmlInputType>;
    }

    public get autocompleteType(): FormControl<EHtmlAutocompleteAttr> {
        return this.elementForm.get('autocompleteType') as FormControl<EHtmlAutocompleteAttr>;
    }

    public get placeholder(): FormControl<string> {
        return this.elementForm.get('placeholder') as FormControl<string>;
    }

    public get value(): FormControl<string> {
        return this.elementForm.get('value') as FormControl<string>;
    }

    public get options(): OptionFormArray {
        return this.elementForm.get('options') as OptionFormArray;
    }

    public get elementFormValue(): FormElement {
        return this.elementForm.value as FormElement;
    }

    public saveLabel(label: string): void {
        this.label.patchValue(label);
    }

    public get isHasEmail(): boolean {
        return !!this.elements.filter((el) => el.type === EFormInputType.email).length;
    }

    public get isHasPhone(): boolean {
        return !!this.elements.filter((el) => el.type === EFormInputType.phone).length;
    }

    public get isHasFile(): boolean {
        if (this.isEduForm) return true;
        return !!this.elements.filter((el) => el.type === EFormInputType.file).length;
    }

    public get isShowDropdown(): boolean {
        return (
            !this.protected.value ||
            (this.type.value !== EFormInputType.email && this.type.value !== EFormInputType.password)
        );
    }

    public get isEmailType(): boolean {
        return this.type.value === EFormInputType.email;
    }

    public get isPhoneType(): boolean {
        return this.type.value === EFormInputType.phone;
    }

    public get isPasswordType(): boolean {
        return this.type.value === EFormInputType.password;
    }

    public get isShowDefaultList(): boolean {
        return (this.isEmailType && this.isHasPhone) || (this.isPhoneType && this.isHasEmail) || !this.protected.value;
    }

    public get disableFilteredContactFields(): boolean {
        return this.type.value === EFormInputType.date || this.type.value === EFormInputType.number;
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public changeBotVariable(chatBot: any, variable: any) {
        const index = this.selectedChatBots.value.findIndex((el) => el.socialNetwork === chatBot.socialNetwork);
        this.selectedChatBots.at(index).patchValue({ variables: variable });

        switch (chatBot.socialNetwork) {
            case ESocialNetworks.telegram:
                this.telegramVariable.patchValue(variable);
                break;
            case ESocialNetworks.instagram:
                this.instagramVariable.patchValue(variable);
                break;
            case ESocialNetworks.viber:
                this.viberVariable.patchValue(variable);
                break;
            case ESocialNetworks.messenger:
                this.messengerVariable.patchValue(variable);
                break;
            case ESocialNetworks.whatsapp:
                this.whatsappVariable.patchValue(variable);
                break;
            case ESocialNetworks.tiktok:
                this.tiktokVariable.patchValue(variable);
                break;
            default:
                break;
        }
    }
}
